import { SettingIcon } from '@/components/SettingIcon';
import { env } from '@/env';
import { useReservationConfig } from '@/features/reservation-setup/hooks/useReservationConfig';
import { LOCALES, useT, type Locale } from '@/i18n';
import { openExternal } from '@/lib/open-external';
import { copyText } from '@/lib/clipboard';
import { showCopyFallbackDialog } from '@/lib/copy-fallback-dialog';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { countryToLocale } from '@/utils/countryToLocale';
import { useSettingsDetailBackLink } from '@/utils/useMediaQuery';
import { BlockTitle, Link as F7Link, List, ListItem, Navbar, Page } from 'framework7-react';
import { useState } from 'react';

// Storefront locales are a fixed set (locale-in-path routing). The default is
// derived per restaurant from its country (see `countryToLocale`) and flagged
// in the picker. Mirrors the salon dialog.
const STOREFRONT_LOCALES: Locale[] = ['de', 'en', 'pl', 'vi'];

// Storefront origin - same env the QR sheet uses. Trailing slash trimmed so we
// can join path segments cleanly. The localhost fallback points at the
// storefront's dev port (3140); real dev/prod come from VITE_STOREFRONT_URL.
const STOREFRONT_BASE = env.storefrontUrl
  ? env.storefrontUrl.replace(/\/+$/, '')
  : 'http://localhost:3140';

/**
 * Derive the franchise + restaurant slugs from the reservation-config booking
 * URL (`{base}/{franchiseSlug}/{restaurantSlug}/reserve`). That URL is the only
 * client-visible source carrying BOTH slugs - the restaurant record exposes its
 * own slug but not the franchise's - so we parse it rather than add an endpoint.
 * Every restaurant owns exactly one reservation-config row, so it's reliable.
 */
function parseSlugs(
  bookingUrl: string | undefined,
): { franchiseSlug: string; restaurantSlug: string } | null {
  if (!bookingUrl) return null;
  try {
    const segments = new URL(bookingUrl).pathname.split('/').filter(Boolean);
    if (segments.length < 2) return null;
    return { franchiseSlug: segments[0], restaurantSlug: segments[1] };
  } catch {
    return null;
  }
}

/**
 * Build a shareable public storefront link in the salon-style, locale-in-path
 * shape: `{base}/{locale}/{franchiseSlug}/{restaurantSlug}{suffix}`. The default
 * locale is included as a path segment (no bare-domain special case). `suffix`
 * targets the tenant sub-routes: '' → hub, '/reserve' → reservation,
 * '/order' → online order.
 */
function buildStorefrontUrl(
  locale: Locale,
  slugs: { franchiseSlug: string; restaurantSlug: string },
  suffix = '',
): string {
  return `${STOREFRONT_BASE}/${locale}/${slugs.franchiseSlug}/${slugs.restaurantSlug}${suffix}`;
}

// ─── LinkRow - plain function returning ListItem (Rule 9: no JSX wrapper) ────
// Invoked as LinkRow({…}) so F7 sees the literal ListItem as a direct child of
// <List> - no leaked <li> bullet. Two-line layout (rsv-stack) matching
// Reservation Setup's valueRow, with copy + open-new-tab icons in `after`.

function LinkRow({
  key,
  label,
  url,
  icon,
  color,
  sl,
}: {
  key: string;
  label: string;
  url: string;
  icon: string;
  color: string;
  sl: ReturnType<typeof useT>['storefrontLink'];
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!url) return;
    // Copy (Clipboard API → legacy execCommand for insecure http), flip the icon
    // to a checkmark, then always show the value in a dialog with an OK button so
    // the operator can read/verify it and copy by hand if needed.
    await copyText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    showCopyFallbackDialog(url, sl.title, sl.okButton);
  };

  const handleOpen = () => {
    if (url) openExternal(url);
  };

  const dimmed = url ? {} : { opacity: 0.4, pointerEvents: 'none' as const };

  return (
    <ListItem
      key={key}
      // NOT a `link` item: the row would render an <a>, but the copy/open
      // buttons in the `after` slot are themselves <a> (F7Link) — nesting
      // interactive content inside an <a> is invalid HTML and breaks hydration.
      // Keep the whole-row tap via onClick on the plain <li> instead.
      onClick={url ? handleOpen : undefined}
      className="rsv-value-row"
      style={url ? { cursor: 'pointer' } : undefined}
    >
      <div slot="media">
        <SettingIcon icon={icon} color={color} />
      </div>
      <div slot="title" className="rsv-stack">
        <div className="rsv-stack-head">
          <span className="rsv-stack-title">{label}</span>
        </div>
        <span
          style={{
            whiteSpace: 'normal',
            fontSize: 13,
            color: 'var(--f7-theme-color)',
            fontWeight: 600,
            wordBreak: 'break-all',
            opacity: url ? 1 : 0.5,
          }}
        >
          {url || sl.linkUnavailable}
        </span>
      </div>
      <div slot="after" style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
        <F7Link
          iconOnly
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            handleCopy();
          }}
          aria-label={sl.copy}
          style={{ display: 'inline-flex', ...dimmed }}
        >
          <i className="f7-icons" style={{ fontSize: 22 }}>
            {copied ? 'checkmark_alt' : 'doc_on_doc'}
          </i>
        </F7Link>
        <F7Link
          iconOnly
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            handleOpen();
          }}
          aria-label={sl.open}
          style={{ display: 'inline-flex', ...dimmed }}
        >
          <i className="f7-icons" style={{ fontSize: 22 }}>
            arrow_up_right_square
          </i>
        </F7Link>
      </div>
    </ListItem>
  );
}

/**
 * Settings → Storefront → "Storefront link". A slide-in detail page (same
 * pattern as every other settings sub-page) for copying/opening the restaurant's
 * public storefront links. Language-aware: the operator picks which locale the
 * shared links point at (DE default) via a compact dropdown, and gets three
 * locale-aware links - homepage/hub, reservation, and online order.
 */
export default function StorefrontLinkPage() {
  const t = useT();
  const sl = t.storefrontLink;
  const settingsBackLink = useSettingsDetailBackLink();
  const { data: config } = useReservationConfig();
  const country = useRestaurantStore((s) => s.restaurantConfig?.country);
  const defaultLocale = countryToLocale(country);
  const [locale, setLocale] = useState<Locale>(defaultLocale);

  const slugs = parseSlugs(config?.bookingUrl);
  const rows = [
    { key: 'homepage', label: sl.homepage, suffix: '', icon: 'house', color: '#0ea5e9' },
    {
      key: 'reservation',
      label: sl.reservation,
      suffix: '/reserve',
      icon: 'calendar',
      color: '#8b5cf6',
    },
    { key: 'onlineOrder', label: sl.onlineOrder, suffix: '/order', icon: 'bag', color: '#f59e0b' },
  ] as const;
  const links = rows.map((r) => ({
    ...r,
    url: slugs ? buildStorefrontUrl(locale, slugs, r.suffix) : '',
  }));

  return (
    <Page name="storefront-link">
      <Navbar title={sl.title} backLink={settingsBackLink} />

      <BlockTitle>{sl.language}</BlockTitle>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem
          title={sl.language}
          smartSelect
          smartSelectParams={{ openIn: 'popover', setValueText: true }}
        >
          <select
            value={locale}
            onChange={(e) => setLocale((e.target as HTMLSelectElement).value as Locale)}
          >
            {STOREFRONT_LOCALES.map((loc) => {
              const info = LOCALES.find((l) => l.value === loc);
              const isDefault = loc === defaultLocale;
              return (
                <option key={loc} value={loc}>
                  {info?.native} - {info?.label}
                  {isDefault ? ` (${sl.default})` : ''}
                </option>
              );
            })}
          </select>
        </ListItem>
      </List>

      <BlockTitle>{sl.linksTitle}</BlockTitle>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        {links.map((link) =>
          // Rule 9: plain function call - F7 sees the literal ListItem, no bullet leak
          LinkRow({
            key: link.key,
            label: link.label,
            url: link.url,
            icon: link.icon,
            color: link.color,
            sl,
          }),
        )}
      </List>
    </Page>
  );
}
